import React, { useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Badge } from "primereact/badge";
import { TabView, TabPanel } from "primereact/tabview";
import { InputTextarea } from "primereact/inputtextarea";
import { InputText } from "primereact/inputtext";
import { ProgressBar } from "primereact/progressbar";
import { makeRequest2 } from "../../../../../utils/makeRequest";
import { toastify } from "../../../../../utils/toast";
import { communicationBaseRoute } from "../../../../../utils/urls";

function ViewSMSModal({
  visible,
  onHide,
  sms,
  facilityId,
  onSMSUpdated,
  onSMSDeleted,
}) {
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    to: "",
    message: "",
  });

  // Initialize form data when SMS changes
  React.useEffect(() => {
    if (sms?.sms) {
      setFormData({
        to: sms.sms.to || "",
        message: sms.sms.message || "",
      });
    }
  }, [sms]);

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Update SMS in queue
  const updateSMS = async () => {
    try {
      setLoading(true);
      const response = await makeRequest2(
        `${communicationBaseRoute}/${facilityId}/sms/${sms._id}`,
        "PUT",
        formData,
      );

      if (response.success) {
        toastify("SMS updated successfully", "success");
        setEditing(false);
        onSMSUpdated();
      } else {
        throw new Error(response.error || "Failed to update SMS");
      }
    } catch (error) {
      toastify("Error updating SMS: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  // Delete SMS from queue
  const deleteSMS = async () => {
    try {
      setLoading(true);
      const response = await makeRequest2(
        `${communicationBaseRoute}/${facilityId}/sms/${sms._id}`,
        "DELETE",
      );

      if (response.success) {
        toastify("SMS deleted successfully", "success");
        onSMSDeleted();
        onHide();
      } else {
        throw new Error(response.error || "Failed to delete SMS");
      }
    } catch (error) {
      toastify("Error deleting SMS: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString();
  };

  // Calculate SMS segments and cost estimation
  const calculateSMSInfo = (message) => {
    if (!message) return { length: 0, segments: 0, cost: 0 };

    const length = message.length;
    const segments = Math.ceil(length / 160);
    const estimatedCost = segments * 0.4; // Assuming KES 0.4 per SMS segment

    return { length, segments, cost: estimatedCost };
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditing(false);
    // Reset form data
    if (sms?.sms) {
      setFormData({
        to: sms.sms.to || "",
        message: sms.sms.message || "",
      });
    }
  };

  // Get SMS info for current message
  const smsInfo = calculateSMSInfo(
    editing ? formData.message : sms?.sms?.message,
  );

  // Modal header
  const modalHeader = (
    <div className="d-flex align-items-center justify-content-between w-100">
      <div className="d-flex align-items-center">
        <i className="ti ti-message-circle me-2"></i>
        <span>SMS Details</span>
      </div>
      <div className="d-flex align-items-center gap-2">
        {sms && (
          <Badge
            value={sms.status || "pending"}
            severity={sms.status === "pending" ? "warning" : "info"}
          />
        )}
      </div>
    </div>
  );

  // Modal footer
  const modalFooter = (
    <div className="d-flex justify-content-between w-100">
      <div>
        <Button
          label="Delete"
          icon="ti ti-trash"
          className="p-button-danger p-button-outlined"
          onClick={deleteSMS}
          loading={loading}
        />
      </div>
      <div className="d-flex gap-2">
        {editing ? (
          <>
            <Button
              label="Cancel"
              icon="ti ti-x"
              className="p-button-text"
              onClick={cancelEdit}
              disabled={loading}
            />
            <Button
              label="Save Changes"
              icon="ti ti-check"
              onClick={updateSMS}
              loading={loading}
            />
          </>
        ) : (
          <>
            <Button
              label="Close"
              icon="ti ti-x"
              className="p-button-text"
              onClick={onHide}
            />
            <Button
              label="Edit"
              icon="ti ti-edit"
              onClick={() => setEditing(true)}
            />
          </>
        )}
      </div>
    </div>
  );

  if (!sms) return null;

  return (
    <Dialog
      visible={visible}
      style={{ width: "90vw", maxWidth: "700px" }}
      header={modalHeader}
      footer={modalFooter}
      onHide={onHide}
      modal
      maximizable
    >
      <div className="sms-details">
        {/* SMS Metadata */}
        <Card className="mb-3">
          <div className="row">
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label fw-semibold">
                  <i className="ti ti-calendar me-2"></i>
                  Queued At
                </label>
                <div className="text-muted">{formatDate(sms.createdAt)}</div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label fw-semibold">
                  <i className="ti ti-id me-2"></i>
                  Queue ID
                </label>
                <div className="text-muted font-monospace">{sms._id}</div>
              </div>
            </div>
          </div>
        </Card>

        {/* SMS Content */}
        <Card>
          <TabView>
            <TabPanel header="SMS Content" leftIcon="ti ti-message-circle me-2">
              <div className="sms-content">
                {/* Recipient */}
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    <i className="ti ti-phone me-2"></i>
                    To (Phone Number)
                  </label>
                  {editing ? (
                    <InputText
                      value={formData.to}
                      onChange={(e) => handleInputChange("to", e.target.value)}
                      className="w-100"
                      placeholder="Recipient phone number"
                    />
                  ) : (
                    <div className="form-control-plaintext font-monospace">
                      {sms.sms?.to || "-"}
                    </div>
                  )}
                </div>

                {/* Message Content */}
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    <i className="ti ti-message me-2"></i>
                    Message
                  </label>
                  {editing ? (
                    <InputTextarea
                      value={formData.message}
                      onChange={(e) =>
                        handleInputChange("message", e.target.value)
                      }
                      rows={6}
                      className="w-100"
                      placeholder="SMS message content"
                      maxLength={1600} // Allow up to 10 SMS segments
                    />
                  ) : (
                    <div className="form-control-plaintext">
                      <pre className="text-wrap">{sms.sms?.message || "-"}</pre>
                    </div>
                  )}
                </div>

                {/* Message Statistics */}
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    <i className="ti ti-chart-bar me-2"></i>
                    Message Statistics
                  </label>
                  <div className="row">
                    <div className="col-md-4">
                      <div className="text-center p-3 border rounded">
                        <div className="h4 mb-1 text-primary">
                          {smsInfo.length}
                        </div>
                        <small className="text-muted">Characters</small>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="text-center p-3 border rounded">
                        <div className="h4 mb-1 text-info">
                          {smsInfo.segments}
                        </div>
                        <small className="text-muted">SMS Segments</small>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="text-center p-3 border rounded">
                        <div className="h4 mb-1 text-success">
                          KES {smsInfo.cost.toFixed(2)}
                        </div>
                        <small className="text-muted">Est. Cost</small>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Character Counter Progress Bar */}
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="form-label fw-semibold mb-0">
                      <i className="ti ti-ruler me-2"></i>
                      Character Usage
                    </label>
                    <small className="text-muted">
                      {smsInfo.length}/160 per segment
                    </small>
                  </div>
                  <ProgressBar
                    value={((smsInfo.length % 160) / 160) * 100}
                    showValue={false}
                    style={{ height: "8px" }}
                    className={
                      smsInfo.length > 160
                        ? "p-progressbar-warning"
                        : "p-progressbar-success"
                    }
                  />
                  {smsInfo.segments > 1 && (
                    <small className="text-warning mt-1 d-block">
                      <i className="ti ti-alert-triangle me-1"></i>
                      This message will be sent as {smsInfo.segments} separate
                      SMS
                    </small>
                  )}
                </div>
              </div>
            </TabPanel>

            {/* Metadata Tab */}
            <TabPanel header="Metadata" leftIcon="ti ti-info-circle me-2">
              <div className="metadata-content">
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Type</label>
                      <div className="text-muted">{sms.type || "sms"}</div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Status</label>
                      <Badge
                        value={sms.status || "pending"}
                        severity={sms.status === "pending" ? "warning" : "info"}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        Facility ID
                      </label>
                      <div className="text-muted font-monospace">
                        {sms.facilityId}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        Created At
                      </label>
                      <div className="text-muted">
                        {formatDate(sms.createdAt)}
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        Updated At
                      </label>
                      <div className="text-muted">
                        {formatDate(sms.updatedAt)}
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Queue ID</label>
                      <div className="text-muted font-monospace">{sms._id}</div>
                    </div>
                  </div>
                </div>

                {/* SMS Analysis */}
                <div className="mt-4">
                  <h6 className="fw-semibold mb-3">
                    <i className="ti ti-analysis me-2"></i>
                    SMS Analysis
                  </h6>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-2">
                        <small className="text-muted">Message Length:</small>
                        <div>{smsInfo.length} characters</div>
                      </div>
                      <div className="mb-2">
                        <small className="text-muted">SMS Segments:</small>
                        <div>{smsInfo.segments} segment(s)</div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-2">
                        <small className="text-muted">Estimated Cost:</small>
                        <div>KES {smsInfo.cost.toFixed(2)}</div>
                      </div>
                      <div className="mb-2">
                        <small className="text-muted">
                          Characters per Segment:
                        </small>
                        <div>160 characters</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TabPanel>
          </TabView>
        </Card>
      </div>
    </Dialog>
  );
}

export default ViewSMSModal;
